import { AccountType, AddressType, Beneficiary } from '@src/utilities/types';
import axiosInstance from "../index";
import { AxiosResponse } from "axios";

/**
 * Creates a new person using the provided user person data and authorization token.
 *
 * @return {Promise<any>} A Promise that resolves with the response data from the server.
 */
export const personCreation = async (): Promise<any> => {
    try {
        const { data } = await axiosInstance.post('/hub/registration/person', {
            registrationType: "company"
        })
        return Promise.resolve(data);
    } catch (error: any) {
        return Promise.reject(error)
    }
}

/**
 * Creates a new person using the provided user person data and authorization token,
 * from partner process.
 *
 * @param {Object} props - The properties of the person to be created.
 * @param {Object} props.personInformations - The informations of the person (civility / email / first name / last name / birth name / mobile phone).
 * @return {Promise<any>} A Promise that resolves with the response data from the server.
 */
type personInformationsProps = {
    civility: string,
    email: string,
    first_name: string,
    last_name: string,
    birth_name?: string,
    mobile_phone?: string,
    sitecode?: string
}
export const personCreationFromPartner = async (personInformations: personInformationsProps): Promise<any> => {
    try {
        const { data } = await axiosInstance.post('/hub/registration/person/partner', {
            ...personInformations
        })
        return Promise.resolve(data);
    } catch (error: any) {
        return Promise.reject(error)
    }
}

/**
 * Updates a person's information in the registration system.
 *
 * @param {object} personUpdateProps - An object containing the properties needed to update a person's information.
 * @param {string} personUpdateProps.key - The key of the property to update.
 * @param {any} personUpdateProps.value - The new value of the property to update.
 * @param {boolean} personUpdateProps.is_psi - A boolean indicating whether the person is a PSI or not.
 * @param {string} personUpdateProps.person_id - The ID of the person.
 * @return {Promise<any>} A promise that resolves with the updated person's information or rejects with an error.
 */
type personUpdateProps = {
    key: string;
    value: any;
    is_psi?: boolean;
    person_id?: string;
    demand_id?: string;
}
export const personUpdate = async ({key, value, is_psi, person_id, demand_id}: personUpdateProps): Promise<any> => {
    try {
        const { data } = await axiosInstance.put('/hub/registration/person', {
            ...is_psi && {is_psi},
            ...person_id && {person_id},
            ...demand_id && {demand_id},
            [key]: value
        })
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Creates a new address for a person in the registration system.
 *
 * @param {personAdressProps} props - An object containing the properties needed to create a new address for a person.
 * @param {AddressType} props.addressValue - The address data to be created.
 * @param {boolean} props.is_psi - A boolean indicating whether the person is a PSI or not.
 * @param {string} props.person_id - The ID of the person.
 * @return {Promise<any>} A promise that resolves to the created address or rejects with an error.
 */
type personAdressProps = {
    addressValue: AddressType;
    is_psi?: boolean;
    person_id?: string;
}
export const personAdress = async ({ addressValue, is_psi, person_id }: personAdressProps): Promise<any> => {
    const { street_number, street_extension, street_name_further, ...rest } = addressValue;
    const values = {
        address_type: rest.address_type,
        street_name: rest.street_name,
        city: { zipcode: rest.city.zipcode, name: rest.city.name },
        country: rest.country,
        ...(street_number && { street_number }),
        ...(street_extension && { street_extension }),
        ...(street_name_further && { street_name_further })
    };
    try {
        const { data } = await axiosInstance.post('/hub/registration/address', {
            'address': values,
            ...is_psi && {is_psi},
            ...person_id && {person_id},
        })
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Updates an existing address for a person in the registration system.
 *
 * @param {updateReprisPersonAddressesProps} props - An object containing the properties needed to update an address for a person.
 * @param {AddressType} props.addressesValue - The address value to be updated.
 * @param {string} props.id - The ID of the address to be updated.
 * @param {boolean} props.is_psi - A boolean indicating whether the person is a PSI or not.
 * @param {string} props.person_id - The ID of the person.
 * @return {Promise<any>} A promise that resolves to the updated address or rejects with an error.
 */
type updateReprisPersonAddressesProps = {
    addressesValue: AddressType;
    id: string;
    is_psi?: boolean;
    person_id?: string;
}
export const updatePersonAddresses = async ({ addressesValue, id, is_psi, person_id }: updateReprisPersonAddressesProps): Promise<any> => {
    const {street_number, street_extension, street_name_further, ...rest } = addressesValue;
    const values = {
        address_type: rest.address_type,
        street_name: rest.street_name,
        city: { zipcode: rest.city.zipcode, name: rest.city.name },
        country: rest.country,
        ...(street_number && { street_number }),
        ...(street_extension && { street_extension }),
        ...(street_name_further && { street_name_further })
    };
    try {
        const { data } = await axiosInstance.put(`/hub/registration/address/${id}`, {
            'address': values,
            ...is_psi && {is_psi},
            ...person_id && {person_id},
        })
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Creates a new company in the registration system.
 *
 * @param {companyCreationProps} basic - An object containing the basic properties of the company to be created.
 * @param {boolean} is_psi - A boolean indicating whether the company is a PSI or not.
 * @param {string} person_id - The ID of the person.
 * @return {Promise<any>} - A promise that resolves with the response data from the server or rejects with an error.
 */
type companyCreationProps = {
    basic: any;
    is_psi: boolean;
    person_id: string;
}
export const companyCreation = async ({basic, is_psi, person_id}: companyCreationProps): Promise<any> => {
    try {
        const { data } = await axiosInstance.post('/hub/registration/company', {
            basic,
            ...is_psi && {is_psi},
            ...person_id && {person_id},
        })
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Updates a company's information in the registration system.
 *
 * @param {companyUpdateProps} companyUpdateProps - An object containing the properties needed to update a company's information.
 * @param {any} companyUpdateProps.value - The new value of the property to update.
 * @param {boolean} companyUpdateProps.is_psi - A boolean indicating whether the company is a PSI or not.
 * @param {string} companyUpdateProps.person_id - The ID of the person.
 * @return {Promise<any>} A promise that resolves with the updated company's information or rejects with an error.
 */
type companyUpdateProps = {
    value: any;
    is_psi?: boolean;
    person_id?: string;
}
export const companyUpdate = async ({ value, is_psi, person_id }: companyUpdateProps): Promise<any> => {
    try {
        const { data } = await axiosInstance.put('/hub/registration/company', {
            ...is_psi && {is_psi},
            ...person_id && {person_id},
            ...value
        })
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * This function adds a new address for a company in the registration system.
 *
 * @param {companyAddressProps} addressValue - The address value to be added.
 * @param {boolean} is_psi - A boolean indicating whether the company is a PSI or not.
 * @param {string} person_id - The ID of the person.
 * @return {Promise<any>} - A promise that resolves with the response data from the server.
 */
type companyAddressProps = {
    addressValue: AddressType;
    is_psi?: boolean;
    person_id?: string;
    demand_id?: string;
}
export const companyAdress = async ({ addressValue, is_psi, person_id, demand_id }: companyAddressProps): Promise<any> => {
    const { street_number, street_extension, street_name_further, ...rest } = addressValue;
    const address = {
        address_type: rest.address_type,
        street_name: rest.street_name,
        city: { zipcode: rest.city.zipcode, name: rest.city.name },
        country: rest.country,
        ...(street_number && { street_number }),
        ...(street_extension && { street_extension }),
        ...(street_name_further && { street_name_further })
    };

    const payload: any = { address };

    if (is_psi) {
        payload.is_psi = is_psi;
        if (person_id) payload.person_id = person_id;
        if (demand_id) payload.demand_id = demand_id;
    }

    try {
        const { data } = await axiosInstance.post('/hub/registration/company/address', payload)
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}


/**
 * Updates an existing company address.
 *
 * @param {companyAddressProps} addressValue - The company address to be updated.
 * @return {Promise<any>} - A promise that resolves to the response data from the server.
 */
export const updateCompanyAddress = async ({ addressValue, is_psi, person_id, demand_id }: companyAddressProps): Promise<any> => {
    const {street_number, street_extension, street_name_further, ...rest } = addressValue;
    const address = {
        address_type: rest.address_type,
        street_name: rest.street_name,
        city: { zipcode: rest.city.zipcode, name: rest.city.name },
        country: rest.country,
        ...(street_number && { street_number }),
        ...(street_extension && { street_extension }),
        ...(street_name_further && { street_name_further })
    };
    const payload: any = { address };
    if (is_psi) {
        payload.is_psi = is_psi;
        payload.person_id = person_id;
        payload.demand_id = demand_id;
    }
    try {
        const { data } = await axiosInstance.put(`/hub/registration/address/${rest.id}`, payload)
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Creates a new demand for opening an account in the registration system.
 *
 * @param {object} props - An object containing the properties needed to create a new demand.
 * @param {string} props.id - The ID of the company.
 * @param {boolean} props.is_psi - A boolean indicating whether the person is a PSI or not.
 * @param {string} props.person_id - The ID of the person.
 * @param {string} props.sitecode - The site code.
 * @param {string} props.manager_id - The ID of the manager.
 * @param {object} props.origin - The marketing tracking informations (tracking, annonce, format, support, referer).
 * @return {Promise<any>} - A promise that resolves with the created demand's information or rejects with an error.
 */
export const openAccountDemand = async ({ id, is_psi, person_id, sitecode, manager_id, origin }: { id: string, is_psi?: boolean, person_id?: string, sitecode?: string, manager_id?: string, origin?: { tracking: string, annonce: string, format: string, support: string, referer: string } }): Promise<any> => {
    try {
        const { data } = await axiosInstance.post('/hub/registration/demand/open-account', {
            ...is_psi && {is_psi},
            ...person_id && {person_id},
            ...sitecode && {sitecode},
            ...manager_id && {manager_id},
            ...origin && {origin},
            registrationType: "company",
            companyId: id
        })
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Adds a new IBAN to the registration system.
 *
 * @param {string} iban - The IBAN to be added.
 * @param {string} demand_id - The ID of the demand associated with the IBAN.
 * @return {Promise<any>} - A promise that resolves with the added IBAN's information or rejects with an error.
 */
export const addNewIban = async ({iban, demand_id}: {iban: string, demand_id: string}): Promise<any> => {
    try {
        const response = await axiosInstance.post(`/hub/registration/ibans`, {
            fromWeb: true,
            demand_id,
            iban
        })
        return Promise.resolve(response);
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * Checks the status of an IBAN (International Bank Account Number) for a specific company.
 *
 * This function sends a GET request to the IBAN status endpoint and returns the response.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.iban - The IBAN to check.
 * @param {string} params.companyId - The ID of the company associated with the IBAN.
 * @returns {Promise<any>} A promise that resolves with the response from the IBAN status endpoint.
 * @throws {Error} Throws an error if the request fails.
 */
export const checkIbanStatus = async ({iban, companyId}: {iban: string, companyId: string}): Promise<any> => {
    try {
        const response = await axiosInstance.get(`/hub/registration/ibans/${iban}/company/${companyId}`)
        return Promise.resolve(response);
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * Calls the API to open a new account.
 *
 * @param {object} accountOpenProps - An object containing the following properties:
 *     - demand_id: string - The demand ID.
 *     - accounts: any - The account details.
 *     - partner_informations: object - The partner information.
 *     - is_psi: boolean - Indicates whether the account is a PSI account.
 *     - person_id: string - The ID of the person associated with the account.
 * @returns {Promise<any>} A promise that resolves to the response data from the API.
 */
type accountOpenProps = {
    demand_id: string
    accounts: AccountType[],
    partner_informations: {
        is_partner: boolean,
        cgp_id: string,
        brand: {
            name: string,
            code: string
        }
    }
    person_id?: string
}
export const accountOpen = async ({demand_id, accounts, partner_informations, person_id}: accountOpenProps): Promise<any> => {
    const payload: Record<string, any> = { 
        demand_id,
        accounts,
    };
    if (partner_informations.is_partner) {
        payload.is_psi = partner_informations.is_partner;
        payload.person_id = person_id;
        payload.partner = {
            cgp: partner_informations.cgp_id,
            brand: partner_informations.brand.code
        }
    }

    try {
        const { data } = await axiosInstance.post('/hub/registration/account', payload)
        return Promise.resolve(data);
    } catch (error: any) {
        return Promise.reject(error)
    }
}

/**
 * Updates the client folder to GP
 * @return {Promise<any>} Promise that resolves with the updated data.
 */
type clientFolderUpdateProps = {
    is_psi?: boolean,
    person_id?: string,
    demand_id: string,
    brand?: string
}
export const clientFolderUpdate = async ({is_psi, person_id, demand_id, brand}: clientFolderUpdateProps): Promise<any> => {
    const payload: Record<string, any> = { 
        registrationType: 'company',
    };
    if (is_psi) {
        payload.is_psi = is_psi;
        payload.demand_id = demand_id;
        payload.person_id = person_id;
        payload.brand = brand;
    }

    try {
        const { data } = await axiosInstance.post('/hub/registration/e-sign/folder', payload)
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Retrieves the status of all documents for company registration
 * @returns {Promise<any>} A promise that resolves with the document status data or rejects with an error
 * @param {string} [demand_id] - The demand ID
 * @throws {Error} When the API request fails
 */
export const getAllDocumentsData = async(demand_id?: string): Promise<any> => {
    try {
        const { data } = await axiosInstance.get(`/hub/registration/e-sign/document/status${demand_id ? `?demand_id=${demand_id}` : ''}`)
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Checks a file by sending it to the registration e-sign endpoint for validation ( GP )
 * @param {string} fileType - The type of file being checked
 * @param {FormData} formData - The form data containing the file to be checked
 * @param {boolean} [isPassport] - Whether the file is a passport
 * @returns {Promise<any>} A promise that resolves with the check result data or rejects with an error
 */
export const fileCheck = async (fileType: string, formData: FormData, isPassport?: boolean): Promise<any> => {
    try {
        const { data } = await axiosInstance.post(`/hub/registration/e-sign/file/${fileType}/check${isPassport ? '?isRectoVerso=true' : ''}`,
            formData, {
                headers: { 
                    'Content-type': 'multipart/form-data'
                }
            }
        )
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Uploads a file associated with a company registration. ( edeal only )
 * This function takes a `FormData` object containing the file and its metadata,
 * and sends it to the specified endpoint for processing. It handles the response
 * and returns the data or an error.
 * @param {FormData} formData - The FormData object containing the file and its details.
 * @returns {Promise<any>} A promise that resolves with the response data from the server
 *                         or rejects with an error if the upload fails.
 * @throws {Error} Throws an error if the upload process encounters an issue.
 */
export const companyFileUpload = async (formData: FormData): Promise<any> => {
    const type = formData.get('type');
    const type_detail = formData.get('type_detail');
    try {
        const { data } = await axiosInstance.post(`/hub/registration/file/${type}/${type_detail}/upload`,
            formData, {
                headers: { 
                    'Content-type': 'multipart/form-data'
                }
            }
        )
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Updates a file associated with a company registration. ( edeal only )
 * @param {FormData} formData - The FormData object containing the file and its details.
 * @returns {Promise<any>} A promise that resolves with the response data from the server
 *                         or rejects with an error if the update fails.
 * @throws {Error} Throws an error if the update process encounters an issue.
 */
export const companyFileUpdate = async (formData: FormData, id: string): Promise<any> => {
    try {
        const { data } = await axiosInstance.put(`/hub/registration/file/${id}`,
            formData, {
                headers: { 
                    'Content-type': 'multipart/form-data'
                }
            }
        )
        return Promise.resolve(data);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
* Get a pending registration demand id
*
* @param email
* @returns
*/
export const getPendingDemandID = async (): Promise<any> => {
    try {
        const { data } = await axiosInstance.get('/hub/registration/demand/id/active?fromWeb=true')
        return Promise.resolve(data);
    } catch (err: any) {
        if (err.response.status !== 404) {
            return Promise.reject(err);
        } else {
            return Promise.resolve(null)
        }
    }
}

/**
* Get all demand date by demand_id
* @param demand_id
* @returns
*/
export const getAllDemandData = async (demand_id: string): Promise<any> => {
    try {
        const { data } = await axiosInstance.get(`/hub/registration/demand/${demand_id}?fromWeb=true`);
        return Promise.resolve(data);
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
* Check demand status by id
* @param id
* @returns
*/
export const checkDemandState = async (id: string): Promise<any> => {
    try {
        const { data } = await axiosInstance.get(`/hub/registration/demand/pending/${id}?fromWeb=true`);
        return Promise.resolve(data);
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * Sends a verification code to the specified email address for registration verification
 * @param {string} email - The email address to send the verification code to
 * @param {boolean} preventEmailSending - A flag indicating whether to send the verification code for verification or not
 * @returns {Promise<AxiosResponse>} A promise that resolves with the API response
 * @throws {Error} If sending the verification code fails
 */
export const sendVerificationCodeToEmail = async (email: string, preventEmailSending: boolean): Promise<AxiosResponse> => {
    try {
        const result = await axiosInstance.post('/hub/registration/verification-code/email/generate', {
            email,
            preventEmailSending,
            registrationType: 'company'
        })
        return Promise.resolve(result);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Verifies the verification code sent to the specified email address during registration
 * @param {string} email - The email address associated with the verification code
 * @param {string} code - The verification code to verify
 * @returns {Promise<AxiosResponse>} A promise that resolves with the API response
 * @throws {Error} If the verification code verification fails
 */
export const verifyCodeFromEmail = async ({email, code}: {email: string, code: string}): Promise<AxiosResponse> => {
    try {
        const result = await axiosInstance.post('/hub/registration/verification-code/email/validate', {
            email,
            code
        })
        return Promise.resolve(result);
    } catch (error) {
        return Promise.reject(error)
    }
}

/**
 * Sends an OTP code to the email address associated with the specified demand ID
 * @param {object} param0 - An object containing the following properties:
 *     - demand_id: string - The demand ID associated with the OTP code
 * @returns {Promise<AxiosResponse>} A promise that resolves with the API response
 * @throws {Error} If sending the OTP code fails
 */
type sendSignatureOTPProps = {
    demand_id: string;
    is_psi?: boolean;
    person_id?: string;
    brand?: string;
}
export const sendSignatureOTP = async({ demand_id, is_psi, person_id, brand }: sendSignatureOTPProps): Promise<AxiosResponse> => {
    const payload: Record<string, any> = { 
        registrationType: 'company',
        fromWeb: true,
        demand_id,
    };
    if (is_psi) {
        payload.is_psi = is_psi;
        payload.person_id = person_id;
        payload.brand = brand;
    }
    try {
        const response = await axiosInstance.post(`/hub/registration/e-sign/send-otp`, payload);
        return Promise.resolve(response);
    } catch (err) {
        return Promise.reject(err);
    }
}
/**
 * Sends an OTP code to sign a contract
 * @param {{demand_id: string}} param0 - The demand ID associated with the contract
 * @returns {Promise<AxiosResponse>} A promise that resolves with the API response
 * @throws {Error} If the OTP code sending fails
 */
type signContractProps = {
    otp_code: string;
    demand_id: string;
    is_psi?: boolean;
    person_id?: string;
    brand?: string;
}
export const signContract = async ({otp_code, demand_id, is_psi, person_id, brand}: signContractProps): Promise<AxiosResponse> => {
    const payload: Record<string, any> = { 
        registrationType: 'company',
        fromWeb: true,
        demand_id,
        otp_code,
    };
    if (is_psi) {
        payload.is_psi = is_psi;
        payload.person_id = person_id;
        payload.brand = brand;
    }
    try {
        const response = await axiosInstance.post(`/hub/registration/e-sign/sign`, payload)
        return Promise.resolve(response);
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * Uploads a contract to the e-sign folder
 * @returns {Promise<any>} A promise that resolves with the API response data or rejects with an error
 * @throws {Error} When the API request fails
 */
type uploadContractProps = {
    is_psi?: boolean;
    demand_id?: string;
    person_id?: string;
    brand?: string;
}
export const uploadContract = async ({ is_psi, demand_id, person_id, brand }: uploadContractProps): Promise<any> => {
    const payload: Record<string, any> = {};
    if (is_psi && brand && person_id && demand_id) {
        payload.is_psi = is_psi;
        payload.brand = brand;
        payload.person_id = person_id;
        payload.demand_id = demand_id;
    }
    try {
        const response = await axiosInstance.post(`/hub/registration/e-sign/folder/upload/contract`, payload)
        return Promise.resolve(response);
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * Retrieves the status of all documents for a company registration
 * @returns {Promise<any>} A promise that resolves with the document status data or rejects with an error
 * @param {string} [person_id] - The person ID associated with the documents
 * @throws {Error} When the API request fails
 */
export const getCompanyDocumentsStatus = async (person_id?: string): Promise<any> => {
    try {
        const response = await axiosInstance.get(`/hub/registration/company/documents${person_id ? `?person_id=${person_id}&is_psi=true` : ''}`)
        return Promise.resolve(response);
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * Adds a new beneficial owner to a company registration
 * @param {{beneficiaries: Beneficiary[], demand_id: string, is_psi?: boolean, person_id?: string}} param0 - An object containing the following properties:
 *     - beneficiaries: Beneficiary[] - An array of beneficial owners to be added
 *     - demand_id: string - The demand ID associated with the company registration
 *     - is_psi: boolean - A boolean indicating whether the company is a PSI or not
 *     - person_id: string - The ID of the person associated with the company
 * @returns {Promise<any>} A promise that resolves with the API response or rejects with an error
 * @throws {Error} When the API request fails
 */
type AddBeneficiariesProps = {
    beneficiaries: Beneficiary[];
    demand_id: string;
    is_psi?: boolean;
    person_id?: string;
};
export const addBeneficiaries = async ({ beneficiaries, demand_id, is_psi, person_id }: AddBeneficiariesProps): Promise<any> => {
    const payload: Record<string, any> = { 
        demand_id,
        beneficial_owners: beneficiaries.map(beneficiary => beneficiary.data)
    };
    if (is_psi) {
        payload.is_psi = is_psi;
        payload.person_id = person_id;
    }
    try {
        const response = await axiosInstance.post(`/hub/registration/company/beneficial-owners`, payload);
        return Promise.resolve(response);
    } catch (error) {
        return Promise.reject(error);
    }
};

/**
 * Retrieves the convention data for a company registration
 * @param {object} param0 - An object containing the following properties:
 *     - demand_id: string - The demand ID associated with the company registration
 *     - is_psi: boolean - A boolean indicating whether the company is a PSI or not
 * @returns {Promise<any>} A promise that resolves with the API response or rejects with an error
 * @throws {Error} When the API request fails
 */
type conventionDataProps = {
    demand_id: string;
    is_psi?: boolean;
    site_code?: string;
}
export const conventionData = async ({ demand_id, is_psi, site_code }: conventionDataProps): Promise<{ blobUrl: string, blob: Blob }> => {
    try {
        const response = await axiosInstance.get(`/hub/registration/demand/${demand_id}/generate-pdf?fromWeb=true${is_psi && site_code ? `&isPsi=true&siteCode=${site_code}` : ''}`, {
            responseType: 'blob'
        });
        if (response.data instanceof Blob) {
            const blobUrl = URL.createObjectURL(response.data);
            return Promise.resolve({ blobUrl, blob: response.data });
        } else {
            throw new Error('Invalid PDF data received');
        }
    } catch (error) {
        console.error('Error fetching convention PDF:', error);
        return Promise.reject(error);
    }
}
